import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useServiceError } from '@/hooks';

import { notification } from 'antd';
import { useIntl } from 'react-intl';
import {
  createInsurerAddress,
  deleteInsurerAddress,
  getInsurerMainAddress,
  listInsurerAddress,
  updateInsurerAddress,
} from './InsurersAddress.service';
import {
  CreateInsurerAddressRequest,
  InsurerAddress,
  UpdateInsurerAddressRequest,
} from './InsurersAddress.types';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';

export function useInsurerAddresses(insurerId: number) {
  const query = useQuery(
    ['insurer/address', insurerId],
    () =>
      !!insurerId ? listInsurerAddress(insurerId) : ({} as InsurerAddress[]),
    { enabled: hasPermissions([Authorities.REGISTER_INSURER_EDIT]) },
  );

  useServiceError(query.error);

  return query;
}

export function useCreateInsurerAddress(insurerId: number) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (obj: CreateInsurerAddressRequest) => createInsurerAddress(insurerId, obj),
    {
      onSuccess() {
        queryClient.invalidateQueries(['insurer/address']);
        notification.success({
          message: intl.formatMessage({
            id: 'insurers.addresses.create.notification.success',
          }),
          description: intl.formatMessage({
            id: 'insurers.addresses.create.notification.successDescription',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateInsurerAddress(insurerId: number) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (obj: UpdateInsurerAddressRequest) => updateInsurerAddress(insurerId, obj),
    {
      onSuccess() {
        queryClient.invalidateQueries(['insurer/address']);
        notification.success({
          message: intl.formatMessage({
            id: 'insurers.addresses.update.notification.success',
          }),
          description: intl.formatMessage({
            id: 'insurers.addresses.update.notification.successDescription',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteInsurerAddress(insurerId: number) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (id: number) => deleteInsurerAddress(insurerId, id),
    {
      onSuccess() {
        queryClient.invalidateQueries(['insurer/address']);
        notification.success({
          message: intl.formatMessage({
            id: 'insurers.addresses.remove.notification.success',
          }),
          description: intl.formatMessage({
            id: 'insurers.addresses.remove.notification.successDescription',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useGetInsurerMainAddress() {
  return useMutation(getInsurerMainAddress);
}
